import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { PollWithDetails, PollOptionWithVotes, PollVote, PollOption } from '../types';

/**
 * Hook to fetch all polls in a chat
 */
export const useChatPolls = (chatId: string | undefined, currentUserId: string | undefined) => {
  const [polls, setPolls] = useState<PollWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPolls = useCallback(async () => {
    if (!chatId || !currentUserId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch all polls for this chat with options and votes
      const { data, error: fetchError } = await supabase
        .from('polls')
        .select(`
          *,
          creator:profiles!polls_created_by_fkey(id, full_name, nickname, avatar_url),
          options:poll_options(
            *,
            votes:poll_votes(*)
          )
        `)
        .eq('chat_id', chatId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      if (data) {
        // Transform data to include vote counts and user voting status
        const transformedPolls: PollWithDetails[] = data.map((poll) => {
          const options: PollOptionWithVotes[] = (poll.options || [])
            .sort((a: PollOption, b: PollOption) => a.option_order - b.option_order)
            .map((option: PollOption & { votes: PollVote[] }) => ({
              ...option,
              vote_count: option.votes?.length || 0,
              voted_by_current_user: option.votes?.some(
                (v: PollVote) => v.user_id === currentUserId
              ) || false,
            }));

          const totalVotes = options.reduce((sum, opt) => sum + opt.vote_count, 0);
          const hasVoted = options.some((opt) => opt.voted_by_current_user);

          return {
            ...poll,
            options,
            total_votes: totalVotes,
            has_voted: hasVoted,
            creator: poll.creator,
          };
        });

        setPolls(transformedPolls);
      }
    } catch (err) {
      console.error('Error fetching chat polls:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch polls');
    } finally {
      setLoading(false);
    }
  }, [chatId, currentUserId]);

  useEffect(() => {
    fetchPolls();

    // Real-time subscription for poll changes
    if (chatId) {
      const channel = supabase
        .channel(`chat-polls-${chatId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'polls', filter: `chat_id=eq.${chatId}` },
          () => fetchPolls()
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'poll_votes' },
          () => fetchPolls()
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [fetchPolls, chatId]);

  return {
    polls,
    loading,
    error,
    refetch: fetchPolls,
    activePolls: polls.filter((p) => !p.is_closed),
    closedPolls: polls.filter((p) => p.is_closed),
  };
};

